import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

const DataTable = ({ data, leaks, selectedPoint, onPointSelect, onExpandTable, onTableRowClick }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [sortField, setSortField] = useState('falconMeas')
  const [sortDirection, setSortDirection] = useState('desc')
  const [filter, setFilter] = useState('all') // all, leaks, good, poor
  
  // Virtualization state
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 })
  const [isLoading, setIsLoading] = useState(false)
  const tableRef = useRef(null)
  const scrollTimeoutRef = useRef(null)
  
  // Virtualization constants
  const ROW_HEIGHT = 40 // Height of each row in pixels
  const BUFFER_SIZE = 10 // Extra rows to render above/below visible area
  const INITIAL_LOAD = 100 // Initial number of rows to load

  // Memoize filtered and sorted data for performance
  const filteredData = useMemo(() => {
    let filtered = data

    switch (filter) {
      case 'leaks':
        filtered = data.filter(point => 
          point.falconMeas > 500 && 
          ['0', '1'].includes(point.falconState) &&
          point.falcon1F > 30
        )
        break
      case 'good':
        filtered = data.filter(point => 
          point.falconState === '1' && 
          point.falcon1F > 30
        )
        break
      case 'poor':
        filtered = data.filter(point => 
          point.falconState !== '1' || 
          point.falcon1F <= 30
        )
        break
      default:
        filtered = data
    }

    return filtered.sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })
  }, [data, filter, sortField, sortDirection])

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    // Reset visible range when sorting changes
    setVisibleRange({ start: 0, end: INITIAL_LOAD })
  }, [sortField, sortDirection])

  // Handle scroll for virtualization
  const handleScroll = useCallback((e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollTop = e.target.scrollTop
      const containerHeight = e.target.clientHeight
      
      // Calculate visible range
      const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_SIZE)
      const visibleRows = Math.ceil(containerHeight / ROW_HEIGHT)
      const end = Math.min(filteredData.length, start + visibleRows + BUFFER_SIZE * 2)
      
      setVisibleRange({ start, end })
    }, 16) // ~60fps throttling
  }, [filteredData.length])

  // Load more data when approaching the end
  const loadMoreData = useCallback(() => {
    if (isLoading || visibleRange.end >= filteredData.length) return
    
    setIsLoading(true)
    
    // Simulate loading delay for smooth UX
    setTimeout(() => {
      const newEnd = Math.min(filteredData.length, visibleRange.end + INITIAL_LOAD)
      setVisibleRange(prev => ({ ...prev, end: newEnd }))
      setIsLoading(false)
    }, 100)
  }, [isLoading, visibleRange.end, filteredData.length])

  // Reset visible range when data changes
  useEffect(() => {
    setVisibleRange({ start: 0, end: INITIAL_LOAD })
  }, [filter, sortField, sortDirection])

  // Function to expand table (exposed to parent)
  const expandTable = useCallback(() => {
    setIsExpanded(true)
  }, [])

  // Expose expand function to parent
  useEffect(() => {
    if (onExpandTable) {
      onExpandTable(expandTable)
    }
  }, [onExpandTable, expandTable])

  // Scroll to selected point
  useEffect(() => {
    if (selectedPoint && filteredData.length > 0 && isExpanded) {
      // Find the index of the selected point in the filtered data
      const pointIndex = filteredData.findIndex(point => 
        point.id === selectedPoint.id || 
        (point.timestamp === selectedPoint.timestamp && 
         Math.abs(point.lat - selectedPoint.lat) < 0.000001 && 
         Math.abs(point.lng - selectedPoint.lng) < 0.000001)
      )
      
      if (pointIndex !== -1) {
        // Calculate scroll position
        const scrollTop = pointIndex * ROW_HEIGHT
        
        // Update visible range to include the selected point
        const start = Math.max(0, pointIndex - BUFFER_SIZE)
        const end = Math.min(filteredData.length, pointIndex + BUFFER_SIZE + 10)
        setVisibleRange({ start, end })
        
        // Scroll to the point after a delay to ensure table is rendered
        setTimeout(() => {
          if (tableRef.current) {
            tableRef.current.scrollTop = scrollTop
          }
        }, 200) // Increased delay to ensure table is fully rendered
      }
    }
  }, [selectedPoint, filteredData, isExpanded])


  const getQualityBadge = (point) => {
    if (point.falconState === '1' && point.falcon1F > 30) {
      return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Good</span>
    } else if (point.falconState === '0') {
      return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Off</span>
    } else {
      return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Poor</span>
    }
  }

  const getLeakBadge = (point) => {
    if (point.falconMeas > 500 && ['0', '1'].includes(point.falconState) && point.falcon1F > 30) {
      const severity = point.falconMeas > 2000 ? 'Very High' :
                     point.falconMeas > 1000 ? 'High' :
                     point.falconMeas > 500 ? 'Medium' : 'Low'
      return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">🚨 {severity}</span>
    }
    return null
  }

  const handleRowClick = useCallback((point) => {
    if (onTableRowClick) {
      onTableRowClick(point)
    }
  }, [onTableRowClick])

  return (
    <div className="border-t bg-white">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            <span>Data Table ({filteredData.length.toLocaleString()} points)</span>
          </button>
          
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="all">All Data</option>
            <option value="leaks">Potential Leaks</option>
            <option value="good">Good Quality</option>
            <option value="poor">Poor Quality</option>
          </select>
          
          <div className="text-xs text-gray-500">
            Showing {visibleRange.start + 1}-{Math.min(visibleRange.end, filteredData.length)} of {filteredData.length.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Virtualized Table */}
      {isExpanded && (
        <div className="max-h-96 overflow-auto" ref={tableRef} onScroll={handleScroll}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th 
                  className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('timestamp')}
                >
                  Timestamp {sortField === 'timestamp' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('falconMeas')}
                >
                  FALCON_MEAS {sortField === 'falconMeas' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-2 text-left">Quality</th>
                <th className="px-3 py-2 text-left">Leak</th>
                <th 
                  className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('falcon1F')}
                >
                  FALCON_1F {sortField === 'falcon1F' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('falcon2F')}
                >
                  FALCON_2F {sortField === 'falcon2F' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-2 text-left">Coordinates</th>
                <th 
                  className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('alt')}
                >
                  Altitude {sortField === 'alt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                {data.some(point => point.sourceFile) && (
                  <th className="px-3 py-2 text-left">Source File</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Spacer for rows above visible area */}
              {visibleRange.start > 0 && (
                <tr>
                  <td colSpan={data.some(point => point.sourceFile) ? 9 : 8} 
                      style={{ height: visibleRange.start * ROW_HEIGHT }} 
                      className="p-0" />
                </tr>
              )}
              
              {/* Visible rows */}
              {filteredData.slice(visibleRange.start, visibleRange.end).map((point, index) => {
                const isSelected = selectedPoint && (
                  point.id === selectedPoint.id || 
                  (point.timestamp === selectedPoint.timestamp && 
                   Math.abs(point.lat - selectedPoint.lat) < 0.000001 && 
                   Math.abs(point.lng - selectedPoint.lng) < 0.000001)
                )
                
                return (
                  <tr 
                    key={point.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`} 
                    style={{ height: ROW_HEIGHT }}
                    onClick={() => handleRowClick(point)}
                  >
                  <td className="px-3 py-2 font-mono text-xs">
                    {point.timestamp}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {point.falconMeas.toFixed(1)}
                  </td>
                  <td className="px-3 py-2">
                    {getQualityBadge(point)}
                  </td>
                  <td className="px-3 py-2">
                    {getLeakBadge(point)}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {point.falcon1F.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {point.falcon2F.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {point.alt.toFixed(1)}m
                  </td>
                  {point.sourceFile && (
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {point.sourceFile}
                    </td>
                  )}
                </tr>
                )
              })}
              
              {/* Spacer for rows below visible area */}
              {visibleRange.end < filteredData.length && (
                <tr>
                  <td colSpan={data.some(point => point.sourceFile) ? 9 : 8} 
                      style={{ height: (filteredData.length - visibleRange.end) * ROW_HEIGHT }} 
                      className="p-0" />
                </tr>
              )}
            </tbody>
          </table>
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="px-4 py-2 text-sm text-blue-600 bg-blue-50 text-center">
              Loading more data...
            </div>
          )}
          
          {/* Load more button */}
          {visibleRange.end < filteredData.length && !isLoading && (
            <div className="px-4 py-2 text-center">
              <button
                onClick={loadMoreData}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Load more data ({filteredData.length - visibleRange.end} remaining)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DataTable
